import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Building2, ChevronDown, ChevronRight, MapPin, Pencil, Phone, Plus, Wrench } from "lucide-react";
import { toast, Toaster } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { client } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { AddEquipmentDialog } from "@/components/AddEquipmentDialog";
import { EditRegisterDialog } from "@/components/EditRegisterDialog";

const blankForms = {
  site: { code: "", name: "", address: "", on_site_contact: "", phone: "", email: "", access_notes: "" },
  area: { name: "", contact_name: "", phone: "", access_notes: "" },
  equipment: { type: "", make: "", model: "", serial_number: "", install_date: "", access_notes: "", custom_fields: [] },
};

export default function CustomerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tree, setTree] = useState(null);
  const [entry, setEntry] = useState(null);
  const [form, setForm] = useState(blankForms.site);
  const [saving, setSaving] = useState(false);
  const [equipmentTypes, setEquipmentTypes] = useState([]);
  const { user } = useAuth();
  const canAddEquipment = ["manager", "service_coordinator"].includes(user?.role);
  const [addPreset, setAddPreset] = useState(null);
  const isManager = user?.role === "manager";
  const [editing, setEditing] = useState(null);

  useEffect(() => {
    client.get("/settings/equipment-types")
      .then(response => setEquipmentTypes(response.data))
      .catch(() => setEquipmentTypes([]));
  }, []);

  const load = async () => {
    try {
      const response = await client.get(`/customers/${id}/tree`);
      setTree(response.data);
    } catch {
      toast.error("Customer record was not found");
      navigate("/customers");
    }
  };

  useEffect(() => { load(); }, [id]);

  const startEntry = (kind, parentId = null) => {
    setEntry({ kind, parentId });
    setForm(blankForms[kind]);
  };

  const change = (key, value) => setForm(current => ({ ...current, [key]: value }));

  const saveEntry = async event => {
    event.preventDefault();
    if (!entry) return;
    const endpoint = {
      site: "/sites",
      area: "/areas",
      equipment: "/equipment",
    }[entry.kind];
    const parent = entry.kind === "site"
      ? { customer_id: id }
      : entry.kind === "area"
        ? { site_id: entry.parentId }
        : { area_id: entry.parentId };
    try {
      setSaving(true);
      await client.post(endpoint, { ...form, ...parent });
      toast.success(`${entry.kind[0].toUpperCase()}${entry.kind.slice(1)} added`);
      setEntry(null);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || `Could not add ${entry.kind}`);
    } finally {
      setSaving(false);
    }
  };

  if (!tree) return <div className="mono text-[12px]" data-testid="customer-detail-loading">Loading…</div>;

  const { customer, sites } = tree;
  return (
    <div data-testid="customer-detail-page">
      <Toaster richColors position="top-right" />
      <button
        onClick={() => navigate("/customers")}
        className="flex items-center gap-1 text-[13px] mb-3"
        style={{ color: "#5F6B7A" }}
        data-testid="customers-back-button"
      >
        <ArrowLeft size={14} /> Customers
      </button>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="mono text-[11px] uppercase tracking-widest" style={{ color: "#5F6B7A" }}>
            {customer.code}
          </div>
          <h1 className="text-[28px] font-bold" style={{ color: "#12161B", letterSpacing: "-0.022em", marginTop: "6px" }}>{customer.name}</h1>
          <div className="mt-1 text-[13.5px]" style={{ color: "#5B6472", lineHeight: 1.5 }}>
            {customer.main_contact || "No main contact"}
            {customer.phone && <PhoneLink phone={customer.phone} testid="customer-detail-phone-link" />}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isManager && (
            <Button variant="outline" className="bf-btn-ghost px-4"
                    onClick={() => setEditing({ kind: "customer", record: customer })} data-testid="edit-customer-button">
              <Pencil size={14} className="mr-2" /> Edit customer
            </Button>
          )}
          {canAddEquipment && (
            <Button variant="outline" className="bf-btn-ghost px-4"
                    onClick={() => setAddPreset({ customerId: id })} data-testid="customer-add-equipment-button">
              <Plus size={15} className="mr-2" /> Add equipment
            </Button>
          )}
          <Button
            className="bf-btn-primary px-4"
            onClick={() => startEntry("site")}
            data-testid="add-site-button"
          >
            <Plus size={15} className="mr-2" /> Add site
          </Button>
        </div>
      </div>
      <AddEquipmentDialog open={!!addPreset} onOpenChange={value => { if (!value) setAddPreset(null); }}
                          preset={addPreset || {}} onSaved={load} />
      {editing && (
        <EditRegisterDialog kind={editing.kind} record={editing.record} onClose={() => setEditing(null)}
                            onSaved={() => { setEditing(null); load(); }} />
      )}

      {entry && (
        <EntryForm
          kind={entry.kind}
          form={form}
          onChange={change}
          onCancel={() => setEntry(null)}
          onSubmit={saveEntry}
          saving={saving}
          equipmentTypes={equipmentTypes}
        />
      )}

      <section className="mt-8 space-y-3" data-testid="customer-tree">
        {!sites.length && (
          <div className="bf-card p-8 text-center text-[13.5px]" style={{ color: "#8A93A1" }}>
            Add this customer’s first site to begin the equipment tree.
          </div>
        )}
        {sites.map(site => (
          <article
            key={site.id}
            className="bg-white rounded-[14px] overflow-hidden"
            style={{ boxShadow: "0 1px 2px rgba(16,24,32,.06), 0 6px 18px rgba(16,24,32,.045)" }}
            data-testid={`site-tree-${site.id}`}
          >
            <div className="p-4 flex items-start justify-between gap-4 flex-wrap">
              <div>
                <div className="flex gap-2 items-center">
                  <Building2 size={16} color="#2563EB" />
                  <strong className="text-[15px]">{site.name}</strong>
                  {site.code && <span className="mono text-[11px]" style={{ color: "#5B6472" }}>{site.code}</span>}
                </div>
                <div className="mt-1 text-[13px]" style={{ color: "#5B6472", lineHeight: 1.5 }}>
                  <MapPin size={12} className="inline mr-1" />{site.address || "No address"}
                </div>
                <div className="mt-1 text-[13px]" style={{ color: "#5B6472", lineHeight: 1.5 }}>
                  {site.on_site_contact || "No site contact"}
                  {site.phone && <PhoneLink phone={site.phone} testid={`site-phone-link-${site.id}`} />}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {isManager && (
                  <Button variant="outline" className="h-9 bf-btn-ghost"
                          onClick={() => setEditing({ kind: "site", record: site })} data-testid={`edit-site-button-${site.id}`}>
                    <Pencil size={13} className="mr-1" /> Edit
                  </Button>
                )}
                {canAddEquipment && (
                  <Button variant="outline" className="h-9 bf-btn-ghost"
                          onClick={() => setAddPreset({ customerId: id, siteId: site.id })}
                          data-testid={`site-add-equipment-button-${site.id}`}>
                    <Plus size={14} className="mr-1" /> Add equipment
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="h-9 bf-btn-ghost"
                  onClick={() => startEntry("area", site.id)}
                  data-testid={`add-area-button-${site.id}`}
                >
                  <Plus size={14} className="mr-1" /> Add area
                </Button>
              </div>
            </div>
            <div className="border-t" style={{ borderColor: "#EFF1F4" }}>
              {site.areas.map(area => (
                <div key={area.id} className="border-b last:border-b-0" style={{ borderColor: "#EFF1F4" }}>
                  <div className="px-5 py-3.5 flex justify-between gap-3 flex-wrap" data-testid={`area-tree-${area.id}`}>
                    <div>
                      <div className="font-medium text-[15px]">{area.name}</div>
                      <div className="text-[13px] mt-0.5" style={{ color: "#5B6472" }}>
                        {area.contact_name || "No area contact"}
                        {area.phone && <PhoneLink phone={area.phone} testid={`area-phone-link-${area.id}`} />}
                        {isManager && (
                          <button className="ml-2 underline text-[12.5px]" style={{ color: "#2563EB" }}
                                  onClick={() => setEditing({ kind: "area", record: area })} data-testid={`edit-area-button-${area.id}`}>
                            Edit area
                          </button>
                        )}
                      </div>
                    </div>
                    {canAddEquipment && (
                    <Button
                      variant="outline"
                      className="h-9 bf-btn-ghost"
                      onClick={() => setAddPreset({ customerId: id, siteId: site.id, areaId: area.id })}
                      data-testid={`add-equipment-button-${area.id}`}
                    >
                      <Plus size={14} className="mr-1" /> Add equipment
                    </Button>
                    )}
                  </div>
                  <div className="px-5 pb-4 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                    {area.equipment.map(item => (
                      <Link
                        key={item.id}
                        to={`/equipment/${item.id}`}
                        className="rounded-[10px] p-3"
                        style={{ background: "#F1F3F6" }}
                        data-testid={`tree-equipment-${item.serial_number}`}
                      >
                        <div className="flex gap-2 items-center">
                          <Wrench size={14} color="#5B6472" />
                          <span className="font-medium text-[13.5px]">{item.type}</span>
                        </div>
                        <div className="mt-1 text-[13px]">{item.make} {item.model}</div>
                        <div className="mono text-[11px] mt-1" style={{ color: "#5B6472" }}>
                          {item.serial_number}
                        </div>
                      </Link>
                    ))}
                    {!area.equipment.length && (
                      <div className="text-[13px] py-2" style={{ color: "#8A93A1" }}>No equipment yet.</div>
                    )}
                  </div>
                </div>
              ))}
              {!site.areas.length && (
                <div className="px-5 py-4 text-[13px]" style={{ color: "#8A93A1" }}>No areas yet.</div>
              )}
            </div>
          </article>
        ))}
      </section>
    </div>
  );
}

function EntryForm({ kind, form, onChange, onCancel, onSubmit, saving, equipmentTypes = [] }) {
  const labels = { site: "site", area: "area", equipment: "equipment record" };
  const typeSpec = equipmentTypes.find(item => item.name === (form.type || "").trim().toLowerCase());
  const extras = typeSpec?.custom_fields || [];
  const valueFor = label => (form.custom_fields || []).find(field => field.label === label)?.value || "";
  const setExtra = (label, value) => {
    const existing = (form.custom_fields || []).filter(field => field.label !== label);
    onChange("custom_fields", [...existing, { label, value }]);
  };
  return (
    <form className="mt-6 bf-card" style={{ padding: "20px" }} onSubmit={onSubmit} data-testid={`new-${kind}-form`}>
      <div className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>
        Add {labels[kind]}
      </div>
      {kind === "site" && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <Field label="Site name" required><Input value={form.name} onChange={e => onChange("name", e.target.value)} data-testid="site-name-input" /></Field>
          <Field label="Site code"><Input value={form.code} onChange={e => onChange("code", e.target.value)} data-testid="site-code-input" /></Field>
          <Field label="Address"><Input value={form.address} onChange={e => onChange("address", e.target.value)} data-testid="site-address-input" /></Field>
          <Field label="Site contact"><Input value={form.on_site_contact} onChange={e => onChange("on_site_contact", e.target.value)} data-testid="site-contact-input" /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={e => onChange("phone", e.target.value)} data-testid="site-phone-input" /></Field>
          <Field label="Email"><Input type="email" value={form.email} onChange={e => onChange("email", e.target.value)} data-testid="site-email-input" /></Field>
          <div className="md:col-span-2 xl:col-span-3"><Field label="Access notes"><Textarea value={form.access_notes} onChange={e => onChange("access_notes", e.target.value)} rows={2} data-testid="site-access-notes-input" /></Field></div>
        </div>
      )}
      {kind === "area" && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Area name" required><Input value={form.name} onChange={e => onChange("name", e.target.value)} data-testid="area-name-input" /></Field>
          <Field label="Area contact"><Input value={form.contact_name} onChange={e => onChange("contact_name", e.target.value)} data-testid="area-contact-input" /></Field>
          <Field label="Phone"><Input value={form.phone} onChange={e => onChange("phone", e.target.value)} data-testid="area-phone-input" /></Field>
          <Field label="Access notes"><Textarea value={form.access_notes} onChange={e => onChange("access_notes", e.target.value)} rows={2} data-testid="area-access-notes-input" /></Field>
        </div>
      )}
      {kind === "equipment" && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          <Field label="Equipment type" required><Input value={form.type} onChange={e => onChange("type", e.target.value)} data-testid="equipment-type-input" /></Field>
          <Field label="Serial number"><Input className="mono" value={form.serial_number} onChange={e => onChange("serial_number", e.target.value)} data-testid="equipment-serial-input" /></Field>
          <Field label="Make"><Input value={form.make} onChange={e => onChange("make", e.target.value)} data-testid="equipment-make-input" /></Field>
          <Field label="Model"><Input value={form.model} onChange={e => onChange("model", e.target.value)} data-testid="equipment-model-input" /></Field>
          <Field label="Install date"><Input type="date" value={form.install_date} onChange={e => onChange("install_date", e.target.value)} data-testid="equipment-install-date-input" /></Field>
          <Field label="Access or location notes"><Textarea value={form.access_notes} onChange={e => onChange("access_notes", e.target.value)} rows={2} data-testid="equipment-access-notes-input" /></Field>
          {extras.map(field => (
            <Field key={field.label} label={field.label}>
              {(field.options || []).length > 0 ? (
                <select
                  className="h-9 w-full rounded-[10px] px-2 text-[13.5px] bg-white"
                  style={{ border: "1px solid #E7EAEE" }}
                  value={valueFor(field.label)}
                  onChange={e => setExtra(field.label, e.target.value)}
                  data-testid={`equipment-extra-${field.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <option value="">Not recorded</option>
                  {field.options.map(option => <option key={option} value={option}>{option}</option>)}
                </select>
              ) : (
                <Input
                  value={valueFor(field.label)}
                  onChange={e => setExtra(field.label, e.target.value)}
                  data-testid={`equipment-extra-${field.label.toLowerCase().replace(/\s+/g, "-")}`}
                />
              )}
            </Field>
          ))}
        </div>
      )}
      <div className="mt-5 flex gap-2">
        <Button className="bf-btn-primary px-4" disabled={saving} data-testid={`save-${kind}-button`}>
          {saving ? "Saving…" : `Save ${labels[kind]}`}
        </Button>
        <Button type="button" variant="outline" className="bf-btn-ghost" onClick={onCancel} data-testid={`cancel-${kind}-button`}>Cancel</Button>
      </div>
    </form>
  );
}

function Field({ label, required, children }) {
  return <div><Label className="font-condensed uppercase tracking-wider text-[12px] font-semibold" style={{ color: "#5B6472" }}>{label} {required && <span style={{ color: "#B42318" }}>*</span>}</Label><div className="mt-1">{children}</div></div>;
}

function PhoneLink({ phone, testid }) {
  return <a href={`tel:${phone}`} className="ml-2 inline-flex items-center gap-1" style={{ color: "#2563EB" }} data-testid={testid}><Phone size={12} />{phone}</a>;
}